import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faComment, // For WhatsApp icon
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import { showToast } from "../../components/toast";

// Main Component
const WhatsAppTemplateManager = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const token = localStorage.getItem("authToken");
  const [senderList, setSenderList] = useState([]);

  // Search filter states
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  useEffect(() => {
    fetchSenderList();
    fetchTemplates();
  }, []);

  const fetchSenderList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/emailconfig/dropdown`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const senderOptions = response.data?.data?.whatsapp?.map((item) => ({
        value: Number(item.value),
        label: item.label,
        sender: item.sender, // include sender here
      }));

      setSenderList(senderOptions || []);
    } catch (err) {
      console.error("Error fetching sender list", err);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setIsRendering(true);
    try {
      const response = await axios.get(
        `${API_URL}settings/template/whatsapp/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setTemplates(response.data.data);
      setTotal(response.data.data.length);
      setPageCount(Math.ceil(response.data.data.length / limit));

      // Delay for skeleton effect
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setIsRendering(false);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Added: Test WhatsApp API integration
  const handleTestWhatsapp = async (templateId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}settings/template/Whatsapp/test`,
        {},
        {
          params: { id: templateId },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      const data = response.data;
      const msg = data.message || "Test WhatsApp sent successfully!";

      Swal.fire({
        title: "Test Response",
        html: msg,
        icon: "info",
      });
    } catch (error) {
      console.error("Error sending Test WhatsApp:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "Failed to send Test WhatsApp. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ End of added function

  // Filter templates based on search criteria
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Template Name filter
      if (
        templateNameFilter &&
        !template.templatename
          .toLowerCase()
          .includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }

      // Campaign filter
      if (
        campaignFilter &&
        !template.campaign.toLowerCase().includes(campaignFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [templates, templateNameFilter, campaignFilter]);

  useEffect(() => {
    if (!templates.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateNameFilter, campaignFilter, templates]);


  // Update pagination based on filtered results
  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit));
    setPage(1); // Reset to first page when filters change
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice(start - 1, end);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${API_URL}settings/template/whatsapp/delete?id=${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          Swal.fire("Deleted!", "The template has been deleted.", "success");
          fetchTemplates(); // Refresh the list
        } catch (error) {
          Swal.fire(
            "Error!",
            "There was an error deleting the template.",
            "error"
          );
        }
      } else {
        Swal.fire("Cancelled", "Your record is safe", "error");
      }
    });
  };


  const getSenderLabel = (senderType) => {
    const sender = senderList.find((s) => s.value === senderType);
    return sender
      ? `${sender.label} (${sender.sender || "-"})`
      : senderType || "-";
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setCampaignFilter("");
    };
    window.addEventListener('clearTemplateWhatsappFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplateWhatsappFilterEvent', handleClearFilters);
    };
  }, []);

  // Skeleton loader for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: 10 }).map((_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
            <div className="skeleton-text" style={{ width: "30px", height: "12px", margin: "0 auto" }}></div>
          </td>
          <td>
            <div className="skeleton-text" style={{ width: "80%", height: "12px" }}></div>
          </td>
          <td>
            <div className="skeleton-text" style={{ width: "60%", height: "12px" }}></div>
          </td>
          <td>
            <div className="skeleton-text" style={{ width: "70%", height: "12px" }}></div>
          </td>
          <td className="text-center">
            <div className="skeleton-text" style={{ width: "30px", height: "15px", borderRadius: "4px", margin: "0 auto" }}></div>
          </td>
          <td style={{ width: "80px" }}>
            <div className="d-flex gap-2">
              <div className="skeleton-text" style={{ width: "30px", height: "15px", borderRadius: "4px" }}></div>
              <div className="skeleton-text" style={{ width: "30px", height: "15px", borderRadius: "4px" }}></div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Whatsapp Template</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper
              module="settings_whatsappnotification"
              action="add"
            >
              <Link
                to={`/Settings/TemplateWhatsappNotification/add/`}
                className="btn btn-default btn-sm me-2"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Whatsapp Template
              </Link>
            </PermissionWrapper>
            {/* Pagination Controls */}
            {!loading && templates.length > 0 && (
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="table-responsive sticky-table-container">
            <style>
              {`
                    /* Sticky header rows */
                    .sticky-whatsapp-table thead tr:first-child th {
                       position: sticky;
                       top: 0;
                       z-index: 20;
                       background: #f8f9fa;
                     }

                    .sticky-whatsapp-table thead tr:nth-child(2) th {
                      position: sticky;
                      top: 30px; /* Adjust based on first header row height */
                      z-index: 19;
                      background: white;
                     }

                    .sticky-table-container {
                       max-height: calc(100vh - 152px);
                      overflow: auto;
                    }

                     .sticky-whatsapp-table th {
                       vertical-align: top;
                      }
                 `}
            </style>
            <table className="table m-b-0 sticky-whatsapp-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>Template Name</th>
                  <th>Campaign</th>
                  <th>Sender Type</th>
                  <th style={{ width: "120px", textAlign: "center" }}>
                    Test WhatsApp {/* ✅ Added header */}
                  </th>
                  <th style={{ width: "80px" }}>Action</th>
                </tr>
                <tr>
                  <th style={{ textAlign: "center" }}></th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search name..."
                        value={templateNameFilter}
                        onChange={(e) =>
                          setTemplateNameFilter(e.target.value)
                        }
                        disabled={loading || isRendering}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search campaign..."
                        value={campaignFilter}
                        onChange={(e) => setCampaignFilter(e.target.value)}
                        disabled={loading || isRendering}
                      />
                    </div>
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => {
                        setTemplateNameFilter("");
                        setCampaignFilter("");
                      }}
                      title="Clear filters"
                      disabled={loading || isRendering}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || isRendering || filterLoading ? (
                  <TableSkeleton />
                ) : paginatedTemplates.length > 0 ? (
                  paginatedTemplates.map((post, index) => (
                    <tr key={post.id}>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{post.templatename}</td>
                      <td>{post.campaign}</td>
                      <td>{getSenderLabel(post.sender_type)}</td>

                      <td className="text-center">
                        <button
                          onClick={() => handleTestWhatsapp(post.id)}
                          className="btn btn-default btn-xs"
                          title="Send Test WhatsApp"
                        >
                          <FontAwesomeIcon icon={faComment} />
                        </button>
                      </td>

                      <td style={{ width: "80px" }}>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="settings_whatsappnotification"
                            action="edit"
                          >
                            <Link
                              className="btn btn-warning btn-xs"
                              to={`/Settings/TemplateWhatsappNotification/edit/${post.id}`}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </PermissionWrapper>
                          <PermissionWrapper
                            module="settings_whatsappnotification"
                            action="delete"
                          >
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDelete(post.id)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {templates.length === 0
                        ? "No templates found. Add Whatsapp Template to get started."
                        : "No templates match your search criteria."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplateManager;